import { Button, formatDate, formatMoney, PageEmpty, PageError, PageLoading, pickerDay } from "@majitap/ui";
import { DateRangePicker } from "@majitap/ui/arc/date-range-picker/date-range-picker";
import { Input } from "@majitap/ui/arc/input/input";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { toInvoiceQuery } from "../../invoices";
import { RouterLink } from "../../router-link";
import { useDebounced } from "../../search";
import { BatchDialog } from "./batch";
import styles from "./invoices.module.css";
import type { Payer } from "./payer";

const DAY = 86_400_000;

/**
 * Who needs invoicing (P8-47, `GET /billing/invoices/to-invoice`): every customer and business
 * with unpaid or partly paid orders whose deliveries aren't on an invoice yet, the longest
 * waiting first. **Invoice** opens Invoice deliveries for that payer, all their orders ticked.
 * The amounts are the API's; nothing is added up here.
 */
export function ToInvoiceView(): ReactNode {
  const format = useCompanyFormat();
  const [query, setQuery] = useState("");
  const q = useDebounced(query.trim());
  const [range, setRange] = useState<{ start: Date; end: Date } | null>(null);
  const period = range ? { start: pickerDay(range.start), end: pickerDay(range.end) } : null;
  const list = useQuery({ ...toInvoiceQuery({ q: q || undefined, period }), placeholderData: keepPreviousData });
  const [invoicing, setInvoicing] = useState<Payer | null>(null);
  const money = (v: string) => formatMoney(v, format);
  const csv = `/v1/admin/billing/invoices/to-invoice?format=csv${
    period ? `&period_start=${period.start}&period_end=${period.end}` : ""
  }${q ? `&q=${encodeURIComponent(q)}` : ""}`;

  return (
    <div className={styles.panel}>
      <div className={styles.pair}>
        <Input
          label="Search"
          placeholder="A customer or business"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <DateRangePicker label="Delivered between" placeholder="Any time" value={range} onChange={setRange} />
      </div>
      {list.status === "pending" ? (
        <PageLoading label="Finding who needs invoicing" />
      ) : list.status === "error" ? (
        <PageError message={messageFor(errorOf(list.error))} onRetry={() => list.refetch()} />
      ) : list.data.rows.length === 0 ? (
        <PageEmpty
          title="Nothing to invoice"
          description="Unpaid and partly paid orders with deliveries not on an invoice show here. Credit customers are invoiced on the 1st of each month."
        />
      ) : (
        <>
          <a className={styles.link} href={csv} download>
            Download (CSV)
          </a>
          <SortableDataTable
            caption="Payers to invoice"
            rowKey={(r) => `${r.payer.type}-${r.payer.id}`}
            rows={list.data.rows}
            itemName={{ one: "payer", other: "payers" }}
            columns={[
              {
                key: "payer",
                label: "Payer",
                sortable: false,
                render: (_v, r) => (
                  <RouterLink
                    href={`/${r.payer.type === "business" ? "businesses" : "customers"}/${r.payer.id}`}
                    className={styles.link}
                  >
                    {r.payer.name ?? "Unnamed"}
                  </RouterLink>
                ),
              },
              { key: "term", label: "Term", sortable: false, render: (_v, r) => r.term?.name ?? "—" },
              { key: "orders", label: "Orders", numeric: true, sortable: false },
              { key: "deliveries", label: "Deliveries", numeric: true, sortable: false },
              { key: "amount", label: "Amount", numeric: true, sortable: false, render: (_v, r) => money(r.amount) },
              {
                key: "credit",
                label: "Already paid",
                numeric: true,
                sortable: false,
                render: (_v, r) => (r.credit === "0.00" ? "—" : money(r.credit)),
              },
              {
                key: "to_invoice",
                label: "To invoice",
                numeric: true,
                sortable: false,
                render: (_v, r) => <strong>{money(r.to_invoice)}</strong>,
              },
              {
                key: "oldest_delivered_at",
                label: "Oldest delivery",
                sortable: false,
                render: (_v, r) => {
                  const days = Math.floor((Date.now() - Date.parse(r.oldest_delivered_at)) / DAY);
                  return `${formatDate(r.oldest_delivered_at, format)} · ${days === 0 ? "today" : days === 1 ? "1 day ago" : `${days} days ago`}`;
                },
              },
              {
                key: "invoice",
                label: "",
                sortable: false,
                render: (_v, r) => (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      setInvoicing({
                        billee: r.payer.type === "business" ? { business_id: r.payer.id } : { customer_id: r.payer.id },
                        label: r.payer.name ?? "Unnamed",
                      })
                    }
                  >
                    Invoice
                  </Button>
                ),
              },
            ]}
          />
          {list.data.more ? (
            <p className={styles.muted}>
              {list.data.more} more {list.data.more === 1 ? "payer" : "payers"}: search or choose a period to see them.
            </p>
          ) : null}
        </>
      )}
      {invoicing ? <BatchDialog payer={invoicing} period={range} onClose={() => setInvoicing(null)} /> : null}
    </div>
  );
}
